<template>
  <HeaderBanner
    class="w-100"
    :headerBannerImage="headerBannerImage"
    :headerBannerTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_TITLE')"
    :headerBannerSubtitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_SUBTITLE')"
  />
  <div class="container px-4 pt-0">
    <div class="d-flex mb-3">
      <b>
        <i class="fa-regular fa-user text-black fs-6" />
      </b>
      <span>
        <b class="text-black fs-6">
          {{ title }}
        </b>
        <br />
        <span class="subtitle">
          {{ t("PAYJOY_CARD_VIEWS.USER_FORM_SUBTITLE") }}
        </span>
      </span>
    </div>
    <div class="mb-3">
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.USER_FORM_FIRST_NAME')"
        :placeholder="t('PAYJOY_CARD_VIEWS.USER_FORM_FIRST_NAME')"
        :fill="true"
        :max-length="100"
        v-model:value="firstNameField"
        :disabled="true"
      />
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.USER_FORM_LAST_NAME')"
        :placeholder="t('PAYJOY_CARD_VIEWS.USER_FORM_LAST_NAME')"
        :fill="true"
        :max-length="100"
        v-model:value="lastNameField"
        :disabled="true"
      />
      <TextInput
        :label="t('PAYJOY_CARD_VIEWS.USER_FORM_IDENTIFICATION_NUMBER')"
        :placeholder="t('PAYJOY_CARD_VIEWS.USER_FORM_IDENTIFICATION_NUMBER')"
        :fill="true"
        :max-length="20"
        v-model:value="identificationField"
        :disabled="identificationFieldDisabled"
        :error="identificationFieldError !== ''"
        :error-message="identificationFieldError"
      />
      <div class="d-flex">
        <div class="field first-field">
          <SelectInput
            class="mt-0"
            :placeholder="t('PAYJOY_CARD_VIEWS.USER_FORM_GENDER')"
            :options="genderOptions"
            :fill="true"
            v-model:value="genderField"
            :error="genderFieldError !== ''"
            :error-message="genderFieldError"
          />
        </div>
        <div class="field">
          <DatePicker
            class="mt-0"
            :placeholder="t('PAYJOY_CARD_VIEWS.USER_FORM_BIRTHDAY')"
            :fill="true"
            v-model:value="birthdayField"
            :error="birthdayFieldError !== ''"
            :error-message="birthdayFieldError"
            :max-date="getMaxAdultDate()"
          />
        </div>
      </div>
      <div class="container-phone-number">
        <TextInputEnhance
          :label="t('PAYJOY_CARD_VIEWS.USER_FORM_PHONE_NUMBER')"
          :fill="true"
          :max-length="10"
          :type="'tel'"
          v-model:value="phoneNumberField"
          :error="phoneNumberFieldError !== ''"
          :errorMessage="phoneNumberFieldError"
          :errorIcon="true"
          :prefix="prefix"
          @keyup.enter="!disabledBtn && onConfirmCick()"
        />
      </div>
    </div>

    <div class="floating-btn">
      <Button
        id="btn-continue"
        class="text-start w-100 p-3"
        :disabled="disabledBtn"
        @click="onConfirmCick"
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{ t("NEXT") }}</span>
        </div>
      </Button>
    </div>
  </div>
</template>

<script lang="ts">
import {
	computed,
	defineComponent,
	ref,
	watch,
	type PropType,
	onMounted,
	inject,
} from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import TextInput from "@/apps/credit-line/components/TextInput.vue";
import SelectInput from "@/apps/credit-line/components/SelectInput.vue";
import DatePicker from "@/apps/credit-line/components/DatePicker.vue";
import type { ICreditCardUserFormData } from "@/apps/credit-line/types/forms";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import TextInputEnhance from "@/shared-components/consumer/TextInputEnhance.vue";
import HeaderBanner from "@/apps/credit-line/components/HeaderBanner.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";

export default defineComponent({
	name: "UserDataForm",
	components: {
		Button,
		TextInput,
		SelectInput,
		DatePicker,
		TextInputEnhance,
		HeaderBanner,
	},
	props: {
		preFilledData: {
			type: Object as PropType<ICreditCardUserFormData | null>,
		},
		customTitle: {
			type: String,
			default: null,
		},
	},
	emits: ["onContinueClick"],
	setup(props) {
		const { t } = useI18n();
		const title = props.customTitle
			? props.customTitle
			: t("PAYJOY_CARD_VIEWS.USER_FORM_TITLE");
		const startedFillout = ref(false);
		const phoneStartedFillout = ref(false);
		// Form fields
		const firstNameField = ref<string>(
			props.preFilledData?.firstName as string
		);
		const lastNameField = ref<string>(props.preFilledData?.lastName as string);
		const identificationField = ref<string>(
			props.preFilledData?.identificationValue || ""
		);
		const identificationFieldError = ref<string>("");
		const taxIdentificationRef = ref();
		const taxIdentificationField = ref<string>(
			props.preFilledData?.taxIdentificationValue || ""
		);
		const taxIdentificationFieldError = ref<string>("");
		const genderField = ref<string>(props.preFilledData?.gender || "");
		const genderFieldError = ref<string>("");
		const identificationFieldDisabled = ref<boolean>(false);
		const birthdayField = ref<string>(props.preFilledData?.birthday || "");
		const birthdayFieldError = ref<string>("");
		const emailField = ref<string>(props.preFilledData?.email || "");
		const prefix = inject("prefix");
		const phoneNumberField = ref<string>(
			props.preFilledData?.phoneNumber || ""
		);
		const phoneNumberFieldError = ref<string>("");
		const headerBannerImage = ref<string>("");
		const genderOptions = [
			{
				text: t("PAYJOY_CARD_VIEWS.USER_FORM_GENDER_FEMALE_OPTION"),
				key: "FEMALE",
			},
			{
				text: t("PAYJOY_CARD_VIEWS.USER_FORM_GENDER_MALE_OPTION"),
				key: "MALE",
			},
		];

		// Block or unblock if we have the CURP
		if (identificationField.value) {
			identificationFieldDisabled.value = true;
			const val = identificationField.value;
			const mask = `************${val.slice(val.length - 4, val.length)}`;
			identificationField.value = mask;
		}

		watch(
			[
				firstNameField,
				lastNameField,
				identificationField,
				genderField,
				birthdayField,
				phoneNumberField,
			],
			(
				[newFirstName, newLastName, newId, newGender, newBirthday, newPhone],
				[oldFirstName, oldLastName, oldId, oldGender, oldBirthday, oldPhone]
			) => {
				// Only consider this a real user change if values are different from their previous values
				const hasUserChanges =
					(oldFirstName !== undefined && newFirstName !== oldFirstName) ||
					(oldLastName !== undefined && newLastName !== oldLastName) ||
					(oldId !== undefined && newId !== oldId) ||
					(oldGender !== undefined && newGender !== oldGender) ||
					(oldBirthday !== undefined && newBirthday !== oldBirthday) ||
					(oldPhone !== undefined && newPhone !== oldPhone);

				// Only fire event if this is an actual user change (not initial setup or prefill)
				// AND if we haven't already recorded a form start
				if (!startedFillout.value && hasUserChanges) {
					startedFillout.value = true;

					// Fire the main form started event just once
					logAnalyticsEvent(
						AnalyticEvents.CreditLine.CcUserDataStartFillout,
						"CC_Details"
					);
				}

				// Track phone field change independently from other fields
				if (
					!phoneStartedFillout.value &&
					oldPhone !== undefined &&
					newPhone !== oldPhone &&
					newPhone !== ""
				) {
					phoneStartedFillout.value = true;

					logAnalyticsEvent(
						AnalyticEvents.CreditLine.CcUserPhoneConfirmationStartFillout,
						"CC_Details"
					);
				}
			}
		);
		const disabledBtn = computed(() => {
			// Check if required fields are empty
			const missingRequiredField = [
				firstNameField,
				lastNameField,
				identificationField.value,
				genderField.value,
				birthdayField.value,
			].some((e) => e === "");

			// Check if phone number is valid (exactly 10 digits)
			const phoneRegex = /^\d{10}$/;
			const invalidPhone = !phoneRegex.test(phoneNumberField.value);

			// Button is disabled if any required field is missing OR phone number is invalid
			return missingRequiredField || invalidPhone;
		});

		onMounted(() => {
			logAnalyticsEvent(
				AnalyticEvents.CreditLine.CcUserDataFormLoaded,
				"CC_Details"
			);

			if (phoneNumberField.value.length > 10) {
				phoneNumberField.value = phoneNumberField.value.slice(-10);
			}

			headerBannerImage.value = useAsset("img/credit-line/cc_data_banner.png");
		});

		return {
			t,
			firstNameField,
			lastNameField,
			identificationField,
			identificationFieldDisabled,
			identificationFieldError,
			taxIdentificationField,
			taxIdentificationFieldError,
			taxIdentificationRef,
			genderField,
			genderFieldError,
			birthdayField,
			birthdayFieldError,
			genderOptions,
			emailField,
			phoneNumberField,
			phoneNumberFieldError,
			disabledBtn,
			title,
			prefix,
			headerBannerImage,
		};
	},
	methods: {
		getMaxAdultDate() {
			const today = new Date();
			today.setFullYear(today.getFullYear() - 18);
			return today;
		},
		onConfirmCick() {
			const requriedFieldError = this.t(
				"PAYJOY_CARD_VIEWS.FORM_REQUIRED_FIELD"
			);
			this.identificationFieldError =
				this.identificationField === "" ? requriedFieldError : "";
			this.phoneNumberFieldError =
				this.phoneNumberField === "" ? requriedFieldError : "";
			this.genderFieldError = this.genderField === "" ? requriedFieldError : "";
			this.birthdayFieldError =
				this.birthdayField === "" ? requriedFieldError : "";

			const phoneRegex = /^\d{10}$/;
			if (!phoneRegex.test(this.phoneNumberField)) {
				this.phoneNumberFieldError = this.t(
					"PAYJOY_CARD_VIEWS.PHONE_NUMBER_ERROR"
				);
			}

			if (!this.disabledBtn && !this.phoneNumberFieldError) {
				const identificationValue =
					this.preFilledData?.identificationValue || // Check if we recived an idNumber from summary
					this.identificationField; // Use regular input field otherwise
				const data: ICreditCardUserFormData = {
					firstName: this.firstNameField,
					lastName: this.lastNameField,
					birthday: this.birthdayField,
					identificationValue: identificationValue as string,
					taxIdentificationValue: identificationValue as string,
					email: this.emailField,
					gender: this.genderField,
					phoneNumber: this.phoneNumberField,
				};
				logAnalyticsEvent(
					AnalyticEvents.CreditLine.CcUserDataContinueClick,
					"CC_Details"
				);
				this.$emit("onContinueClick", data);
			}
		},
	},
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.field {
  width: 60%;
  margin-top: -4px;
}
.first-field {
  margin-right: 8px;
}
.field-attachment {
  width: 60%;
}
.subtitle {
  font-size: 13px;
}
.floating-btn-placeholder,
.floating-btn {
  height: 80px;
  width: 100%;
}
.floating-btn {
  background-color: white;
  left: 0;
  bottom: 0;
  z-index: 200;
}
.error-label {
  color: $pj-color-red;
  font-size: 14px;
}
.container-phone-number {
  margin-top: -4px;
  width: 100%;
  padding-bottom: 48px;
}
</style>
